import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Search } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const SB = { draft: "bg-[#1A1B1A]/5 text-[#757675]", issued: "bg-blue-50 text-blue-600", unpaid: "bg-amber-50 text-amber-600", paid: "bg-[#205B5A]/10 text-[#205B5A]", "partially-paid": "bg-amber-50 text-amber-600", cancelled: "bg-red-50 text-red-400" };

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [patients, setPatients] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [patientSearch, setPatientSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [patientAppts, setPatientAppts] = useState([]);
  const [selectedAppts, setSelectedAppts] = useState(new Set());
  const [createForm, setCreateForm] = useState({ patient_id: "", items: [], notes: "" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const params = filter !== "all" ? `?status=${filter}` : "";
    try { const { data } = await api.get(`/invoices${params}`); setInvoices(data.invoices); setTotal(data.total); } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);

  const searchP = async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    try { const { data } = await api.get(`/patients?search=${q}&limit=6`); setSearchResults(data.patients); } catch {}
  };
  useEffect(() => { const t = setTimeout(() => searchP(patientSearch), 300); return () => clearTimeout(t); }, [patientSearch]);

  // Load invoiceable appointments when patient is selected
  const selectPatient = async (p) => {
    setCreateForm(f => ({ ...f, patient_id: p.id, items: [] }));
    setPatientSearch(`${p.first_name} ${p.last_name}`);
    setSearchResults([]);
    setSelectedAppts(new Set());
    try {
      const [apptRes, invRes, typesRes] = await Promise.all([
        api.get(`/appointments?patient_id=${p.id}&status=completed&limit=50`),
        api.get(`/invoices?patient_id=${p.id}&limit=200`),
        api.get("/appointment-types"),
      ]);
      const invoicedApptIds = new Set(invRes.data.invoices.map(i => i.appointment_id).filter(Boolean));
      const typeMap = {};
      typesRes.data.appointment_types.forEach(t => { typeMap[t.name] = t; });
      // Enrich with prices from appointment types
      const uninvoiced = apptRes.data.appointments.filter(a => !invoicedApptIds.has(a.id)).map(a => {
        if (!a.price && a.appointment_type_name && typeMap[a.appointment_type_name]) {
          return { ...a, price: typeMap[a.appointment_type_name].price || 0 };
        }
        return a;
      });
      setPatientAppts(uninvoiced);
    } catch { setPatientAppts([]); }
  };

  const toggleAppt = (appt) => {
    setSelectedAppts(prev => {
      const next = new Set(prev);
      if (next.has(appt.id)) {
        next.delete(appt.id);
        setCreateForm(f => ({ ...f, items: f.items.filter(i => i._apptId !== appt.id) }));
      } else {
        next.add(appt.id);
        const price = appt.price || 0;
        const desc = `${appt.appointment_type_name || "Appointment"} (${appt.duration_minutes || 30} min) — ${appt.date}`;
        setCreateForm(f => ({ ...f, items: [...f.items, { description: desc, quantity: 1, unit_price: price, _apptId: appt.id }] }));
      }
      return next;
    });
  };

  const addManualItem = () => setCreateForm(f => ({ ...f, items: [...f.items, { description: "", quantity: 1, unit_price: 0 }] }));
  const removeItem = (i) => {
    const item = createForm.items[i];
    if (item?._apptId) setSelectedAppts(prev => { const n = new Set(prev); n.delete(item._apptId); return n; });
    setCreateForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  };
  const updateItem = (i, key, val) => setCreateForm(f => ({ ...f, items: f.items.map((item, idx) => idx === i ? { ...item, [key]: val } : item) }));

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/invoices", createForm);
      setShowCreate(false);
      navigate(`/app/invoices/${data.id}`);
    } catch {}
  };

  const totalCreating = createForm.items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);
  const FILTERS = ["all", "draft", "issued", "unpaid", "paid", "cancelled"];

  return (
    <div className="p-6 md:p-8 max-w-7xl" data-testid="invoices-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium text-[#1A1B1A]" style={H}>Invoices</h1>
          <p className="text-sm text-[#757675]">{total} invoices</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-invoice-btn"
          className="bg-[#205B5A] text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-[#174544] flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> Create invoice
        </button>
      </div>

      <div className="flex gap-1.5 mb-5 overflow-x-auto pb-1">
        {FILTERS.map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`invoice-filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-medium capitalize whitespace-nowrap transition-colors ${filter === s ? "bg-[#205B5A] text-white" : "bg-white text-[#757675] border border-[#1A1B1A]/10 hover:border-[#1A1B1A]/20"}`}>{s === "partially-paid" ? "Partial" : s}</button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-[#1A1B1A]/[0.06] overflow-hidden">
        <table className="w-full">
          <thead><tr className="border-b border-[#1A1B1A]/[0.06]">
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Invoice</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Patient</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase hidden md:table-cell">Date</th>
            <th className="text-right px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Total</th>
            <th className="text-left px-5 py-3 text-[10px] font-medium text-[#757675] uppercase">Status</th>
          </tr></thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.id} className="border-b border-[#1A1B1A]/[0.03] hover:bg-[#FAF9F6] cursor-pointer" onClick={() => navigate(`/app/invoices/${inv.id}`)}>
                <td className="px-5 py-3 text-sm font-medium text-[#1A1B1A]">{inv.invoice_number}</td>
                <td className="px-5 py-3 text-sm text-[#4A4B4A]">{inv.patient_name}</td>
                <td className="px-5 py-3 text-sm text-[#757675] hidden md:table-cell">{inv.issue_date || inv.created_at?.split("T")[0]}</td>
                <td className="px-5 py-3 text-sm font-semibold text-[#1A1B1A] text-right">£{inv.total?.toFixed(2)}</td>
                <td className="px-5 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${SB[inv.status] || SB.draft}`}>{inv.status}</span></td>
              </tr>
            ))}
            {invoices.length === 0 && !loading && <tr><td colSpan={5} className="px-5 py-12 text-center text-sm text-[#757675]">No invoices found</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Create invoice modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" data-testid="create-invoice-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-medium text-[#1A1B1A] mb-4" style={H}>Create invoice</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              {/* Patient search */}
              <div>
                <label className="block text-xs font-medium text-[#757675] mb-1.5">Patient</label>
                {createForm.patient_id ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#205B5A]/20 bg-[#205B5A]/[0.03]">
                    <span className="text-sm font-medium text-[#1A1B1A] flex-1">{patientSearch}</span>
                    <button type="button" onClick={() => { setCreateForm(f => ({ ...f, patient_id: "", items: [] })); setPatientSearch(""); setPatientAppts([]); setSelectedAppts(new Set()); }} className="text-[#757675]"><span className="text-xs">×</span></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#757675]" />
                    <input value={patientSearch} onChange={e => setPatientSearch(e.target.value)} placeholder="Search patient..." data-testid="invoice-patient-search"
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white border rounded-xl shadow-lg max-h-40 overflow-y-auto">
                        {searchResults.map(p => (
                          <button type="button" key={p.id} onClick={() => selectPatient(p)}
                            className="w-full text-left px-3 py-2 hover:bg-[#FAF9F6] text-sm">{p.first_name} {p.last_name}{p.date_of_birth ? <span className="text-[10px] text-[#757675] ml-2">DOB: {p.date_of_birth}</span> : null}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Invoiceable appointments */}
              {createForm.patient_id && patientAppts.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-[#757675] mb-1.5">Select treatments to invoice</label>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {patientAppts.map(a => (
                      <label key={a.id} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${selectedAppts.has(a.id) ? "border-[#205B5A]/30 bg-[#205B5A]/[0.03]" : "border-[#1A1B1A]/8 hover:bg-[#FAF9F6]"}`} data-testid={`appt-invoice-${a.id}`}>
                        <input type="checkbox" checked={selectedAppts.has(a.id)} onChange={() => toggleAppt(a)}
                          className="rounded border-[#1A1B1A]/20 text-[#205B5A] focus:ring-[#205B5A] w-4 h-4" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#1A1B1A]">{a.appointment_type_name || "Appointment"}</p>
                          <p className="text-[10px] text-[#757675]">{a.date} {a.start_time} · {a.practitioner_name}</p>
                        </div>
                        <span className="text-xs font-semibold text-[#205B5A]">£{(a.price || 0).toFixed(2)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {createForm.patient_id && patientAppts.length === 0 && (
                <p className="text-xs text-[#757675] bg-[#FAF9F6] px-3 py-2.5 rounded-xl">No uninvoiced completed appointments found for this patient.</p>
              )}

              {/* Generated + manual items */}
              {createForm.items.length > 0 && (
                <div>
                  <label className="block text-xs font-medium text-[#757675] mb-1.5">Invoice items</label>
                  <div className="space-y-2">
                    {createForm.items.map((item, i) => (
                      <div key={i} className="flex gap-2 items-end">
                        <input value={item.description} onChange={e => updateItem(i, "description", e.target.value)} placeholder="Service / item" required
                          className="flex-1 px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs focus:outline-none focus:border-[#205B5A]" />
                        <input type="number" min={1} value={item.quantity} onChange={e => updateItem(i, "quantity", Number(e.target.value))}
                          className="w-14 px-2 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs text-center" />
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-[#757675]">£</span>
                          <input type="number" step="0.01" min={0} value={item.unit_price} onChange={e => updateItem(i, "unit_price", Number(e.target.value))}
                            className="w-20 pl-5 pr-2 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs text-right" />
                        </div>
                        <span className="text-xs font-medium text-[#1A1B1A] w-16 text-right">£{(item.quantity * item.unit_price).toFixed(2)}</span>
                        <button type="button" onClick={() => removeItem(i)} className="text-[#757675] hover:text-red-500 text-xs px-1">×</button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {createForm.patient_id && <button type="button" onClick={addManualItem} className="text-[10px] font-medium text-[#205B5A] hover:text-[#174544]">+ Add manual item</button>}

              {/* Total */}
              {createForm.items.length > 0 && (
                <div className="flex justify-between items-center px-3 py-2.5 bg-[#FAF9F6] rounded-xl">
                  <span className="text-xs font-medium text-[#757675]">Total</span>
                  <span className="text-base font-semibold text-[#1A1B1A]" style={H}>£{totalCreating.toFixed(2)}</span>
                </div>
              )}

              <textarea value={createForm.notes} onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Notes (optional)"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />

              <div className="flex justify-end gap-3 pt-1">
                <button type="button" onClick={() => { setShowCreate(false); setPatientAppts([]); setSelectedAppts(new Set()); }} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
                <button type="submit" disabled={!createForm.patient_id || createForm.items.length === 0} data-testid="save-invoice-btn"
                  className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40">Create invoice</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
